/**
 * Bakehouse post-visit feedback (Klaviyo → Shopify).
 * Query params: recharge_customer_id | customerId | customer_id | rc_id, email (URL-encoded e.g. %40 for @).
 */
(function () {
  function getQueryParams() {
    const p = new URLSearchParams(window.location.search);
    const emailRaw = (p.get('email') || '').trim();
    return {
      rechargeCustomerId:
        p.get('recharge_customer_id') || p.get('customerId') || p.get('customer_id') || p.get('rc_id') || '',
      email: emailRaw ? emailRaw.toLowerCase() : '',
      source: p.get('source') || 'klaviyo',
    };
  }

  function buildReviewText(comment) {
    return (comment && comment.trim()) || '';
  }

  class BakehouseFeedbackRoot extends HTMLElement {
    connectedCallback() {
      this.apiEndpoint = (this.dataset.apiEndpoint || '').replace(/\/$/, '');
      this.googleUrl = this.dataset.googleReviewUrl || '';
      this.yelpUrl = this.dataset.yelpReviewUrl || '';
      this.params = getQueryParams();

      this.rating = 0;
      this.feedbackId = null;

      this.innerHTML = this.template();
      this.bind();
    }

    template() {
      const hSize = this.dataset.headingSize || 'h1';
      return `
        <div class="bf-card" data-role="card">
          <h2 class="bf-heading ${hSize}" data-role="heading"></h2>
          <p class="bf-sub" data-role="subheading"></p>

          <div class="bf-stars" data-role="stars" aria-label="Rating"></div>
          <p class="bf-hint" data-role="hint">Tap a star to get started</p>

          <div class="bf-comment-section" data-role="comment-section" hidden>
            <label class="bf-label" data-role="comment-label">
              <span>Your thoughts</span>
              <textarea class="bf-textarea" data-role="comment" rows="3" placeholder="Tell us about your visit…"></textarea>
            </label>
          </div>

          <div class="bf-actions" data-role="actions" hidden>
            <button type="button" class="bf-btn bf-btn-primary" data-role="submit">Submit Feedback</button>
          </div>

          <div class="bf-status" data-role="status" hidden></div>
        </div>

        <div class="bf-success" data-role="success" hidden>
          <span class="bf-success-icon">✨</span>
          <p class="bf-success-title ${hSize}">Thank You!</p>
          <p class="bf-success-msg">Your feedback means a lot to us and helps us make the Bakehouse even better.</p>
          <div class="bf-review-prompt" data-role="review-prompt" hidden>
            <p class="bf-review-prompt-title">Would you mind sharing the love?</p>
            <p class="bf-review-prompt-hint">We'll copy your review so you can <strong>paste it right away</strong>.</p>
            <div class="bf-review-btns">
              <button type="button" class="bf-btn bf-btn-secondary" data-action="open-google">Open in Google</button>
              <button type="button" class="bf-btn bf-btn-secondary" data-action="open-yelp">Open in Yelp</button>
            </div>
          </div>
        </div>

        <div class="bf-toast" data-role="toast" hidden>
          <span class="bf-toast-icon">✅</span>
          <span class="bf-toast-msg">Review copied to clipboard!</span>
        </div>
      `;
    }

    bind() {
      this.el = {
        card: this.querySelector('[data-role="card"]'),
        heading: this.querySelector('[data-role="heading"]'),
        sub: this.querySelector('[data-role="subheading"]'),
        stars: this.querySelector('[data-role="stars"]'),
        hint: this.querySelector('[data-role="hint"]'),
        commentSection: this.querySelector('[data-role="comment-section"]'),
        commentLabel: this.querySelector('[data-role="comment-label"]'),
        comment: this.querySelector('[data-role="comment"]'),
        actions: this.querySelector('[data-role="actions"]'),
        submit: this.querySelector('[data-role="submit"]'),
        status: this.querySelector('[data-role="status"]'),
        success: this.querySelector('[data-role="success"]'),
        reviewPrompt: this.querySelector('[data-role="review-prompt"]'),
        toast: this.querySelector('[data-role="toast"]'),
      };

      this.el.heading.textContent = this.dataset.heading || 'How was your visit?';
      this.el.sub.textContent = this.dataset.subheading || '';

      for (let i = 1; i <= 5; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'bf-star-btn';
        b.setAttribute('data-star', String(i));
        b.setAttribute('aria-label', `${i} star${i > 1 ? 's' : ''}`);
        b.innerHTML = '<span class="bf-star-icon">★</span>';
        b.addEventListener('click', () => this.setRating(i));
        this.el.stars.appendChild(b);
      }

      this.el.submit.addEventListener('click', () => this.submit());
      this.querySelector('[data-action="open-google"]')?.addEventListener('click', () => this.openReviewSite('google'));
      this.querySelector('[data-action="open-yelp"]')?.addEventListener('click', () => this.openReviewSite('yelp'));
    }

    setRating(n) {
      this.rating = n;
      this.querySelectorAll('.bf-star-btn').forEach((btn) => {
        const v = parseInt(btn.getAttribute('data-star'), 10);
        btn.classList.toggle('is-active', v <= n);
      });

      this.el.commentSection.hidden = false;
      this.el.commentSection.classList.add('bf-fade-in');
      this.el.actions.hidden = false;
      this.el.actions.classList.add('bf-fade-in');

      if (this.el.hint) this.el.hint.hidden = true;
    }

    buildComment() {
      return (this.el.comment && this.el.comment.value.trim()) || '';
    }

    async submit() {
      if (!this.rating) return;
      this.el.status.hidden = true;
      this.el.submit.disabled = true;
      this.el.submit.textContent = 'Sending…';
      const comment = this.buildComment();
      try {
        const res = await fetch(`${this.apiEndpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            rechargeCustomerId: this.params.rechargeCustomerId || undefined,
            email: this.params.email || undefined,
            rating: this.rating,
            comment: comment || undefined,
            source: this.params.source || 'klaviyo',
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || data.message || 'Something went wrong');
        }
        this.feedbackId = data.feedbackId;
        this.el.card.hidden = true;
        this.el.success.hidden = false;
        this.el.success.classList.add('bf-fade-in');
        if (this.rating >= 4 && (this.googleUrl || this.yelpUrl)) {
          this.el.reviewPrompt.hidden = false;
          if (!this.googleUrl) this.querySelector('[data-action="open-google"]')?.setAttribute('hidden', '');
          if (!this.yelpUrl) this.querySelector('[data-action="open-yelp"]')?.setAttribute('hidden', '');
        }
      } catch (e) {
        this.el.status.textContent = e.message || 'Submit failed — please try again.';
        this.el.status.hidden = false;
      } finally {
        this.el.submit.disabled = false;
        this.el.submit.textContent = 'Submit Feedback';
      }
    }

    async patchExternal(target) {
      if (!this.feedbackId || !this.apiEndpoint) return;
      const url = `${this.apiEndpoint}/${this.feedbackId}`;
      try {
        await fetch(url, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ externalReviewClicked: target }),
        });
      } catch (_) {
        /* non-blocking */
      }
    }

    async openReviewSite(target) {
      const url = target === 'google' ? this.googleUrl : this.yelpUrl;
      if (!url) return;

      this.patchExternal(target);

      const comment = buildReviewText(this.buildComment());
      if (comment) {
        try {
          await navigator.clipboard.writeText(comment);
          this.showToast();
        } catch (_) {
          /* clipboard failed — still open the page */
        }
      }

      setTimeout(() => {
        window.open(url, '_blank', 'noopener,noreferrer');
      }, 600);
    }

    showToast() {
      if (!this.el.toast) return;
      this.el.toast.hidden = false;
      this.el.toast.classList.remove('bf-toast-out');
      this.el.toast.classList.add('bf-toast-in');
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => {
        this.el.toast.classList.remove('bf-toast-in');
        this.el.toast.classList.add('bf-toast-out');
        setTimeout(() => { this.el.toast.hidden = true; }, 350);
      }, 8000);
    }
  }

  customElements.define('bakehouse-feedback-root', BakehouseFeedbackRoot);
})();
